import { useState, useRef, useCallback, useEffect } from 'react';
import { Timeline } from '../lib/timeline';
import { type Drill, type XY } from '../types';

export function useDrillPlayback(drill: Drill) {
    const [previewTimeline, setPreviewTimeline] = useState<Timeline | null>(null);
    const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
    const [previewState, setPreviewState] = useState<{
        actorPositions: Map<string, XY>;
        ballOwner: string | null;
        flyingBall: { from: XY; to: XY; progress: number; via?: XY[] } | null;
        currentTime: number;
        totalDuration: number;
        currentStepIndex: number;
        stepProgress: number;
    } | null>(null);

    const previewFrameRef = useRef<number | null>(null);
    const previewLastTimeRef = useRef<number>(0);
    const previewPlayingRef = useRef(false);
    const prevDrillRef = useRef<Drill>(drill);

    useEffect(() => {
        return () => {
            if (previewFrameRef.current !== null) {
                cancelAnimationFrame(previewFrameRef.current);
            }
        };
    }, []);

    const stopPreviewPlayback = useCallback((clearState = false) => {
        if (previewFrameRef.current !== null) {
            cancelAnimationFrame(previewFrameRef.current);
            previewFrameRef.current = null;
        }
        previewPlayingRef.current = false;
        setIsPreviewPlaying(false);
        if (clearState) {
            setPreviewTimeline(null);
            setPreviewState(null);
        }
    }, []);

    useEffect(() => {
        if (prevDrillRef.current !== drill && previewTimeline) {
            stopPreviewPlayback(true);
        }
        prevDrillRef.current = drill;
    }, [drill, previewTimeline, stopPreviewPlayback]);

    const updatePreviewStateFromTimeline = useCallback((timeline: Timeline) => {
        setPreviewState({
            actorPositions: new Map(timeline.state.actorPositions),
            ballOwner: timeline.state.ballOwner,
            flyingBall: timeline.state.flyingBall
                ? {
                    from: { ...timeline.state.flyingBall.from },
                    to: { ...timeline.state.flyingBall.to },
                    progress: timeline.state.flyingBall.progress,
                    via: timeline.state.flyingBall.via ? timeline.state.flyingBall.via.map(pt => ({ ...pt })) : undefined,
                }
                : null,
            currentTime: timeline.state.currentTime,
            totalDuration: timeline.state.totalDuration,
            currentStepIndex: timeline.state.currentStepIndex,
            stepProgress: timeline.state.stepProgress,
        });
    }, []);

    const applyPreviewAtTime = useCallback((time: number) => {
        if (previewFrameRef.current !== null) {
            cancelAnimationFrame(previewFrameRef.current);
            previewFrameRef.current = null;
        }
        previewPlayingRef.current = false;
        setIsPreviewPlaying(false);

        const timeline = new Timeline(drill);
        timeline.seek(time);
        setPreviewTimeline(timeline);
        updatePreviewStateFromTimeline(timeline);
    }, [drill, updatePreviewStateFromTimeline]);

    const handlePreviewPlay = useCallback(() => {
        if (isPreviewPlaying || drill.timeline.length === 0) return;

        let timeline = previewTimeline;
        if (!timeline) {
            timeline = new Timeline(drill);
            timeline.seek(0);
            setPreviewTimeline(timeline);
        }
        if (timeline.state.currentTime >= timeline.totalDuration) {
            timeline.seek(0);
        }
        updatePreviewStateFromTimeline(timeline);
        if (timeline.totalDuration === 0) {
            return;
        }

        previewPlayingRef.current = true;
        setIsPreviewPlaying(true);
        previewLastTimeRef.current = performance.now();

        const animate = (currentTime: number) => {
            const delta = currentTime - previewLastTimeRef.current;
            previewLastTimeRef.current = currentTime;
            if (!timeline) return;

            const nextTime = Math.min(timeline.state.currentTime + delta, timeline.totalDuration);
            timeline.seek(nextTime);
            updatePreviewStateFromTimeline(timeline);

            if (nextTime >= timeline.totalDuration) {
                stopPreviewPlayback();
                setIsPreviewPlaying(false);
                return;
            }

            if (!previewPlayingRef.current) return;
            previewFrameRef.current = requestAnimationFrame(animate);
        };

        previewFrameRef.current = requestAnimationFrame(animate);
    }, [drill, isPreviewPlaying, previewTimeline, stopPreviewPlayback, updatePreviewStateFromTimeline]);

    const handlePreviewStop = useCallback(() => {
        stopPreviewPlayback(false);
    }, [stopPreviewPlayback]);

    const handlePreviewReset = useCallback(() => {
        if (!previewTimeline) {
            const freshTimeline = new Timeline(drill);
            freshTimeline.seek(0);
            setPreviewTimeline(freshTimeline);
            updatePreviewStateFromTimeline(freshTimeline);
            setIsPreviewPlaying(false);
            return;
        }
        stopPreviewPlayback();
        previewTimeline.seek(0);
        updatePreviewStateFromTimeline(previewTimeline);
    }, [drill, previewTimeline, stopPreviewPlayback, updatePreviewStateFromTimeline]);

    return {
        previewTimeline,
        isPreviewPlaying,
        previewState,
        play: handlePreviewPlay,
        stop: handlePreviewStop,
        reset: handlePreviewReset,
        applyPreviewAtTime,
    };
}
